/**
 * Pure helpers for building language-aware reason-code lookup maps used by
 * the Delivery RPM One Way and Route Roadblocks tables.
 *
 * Both `/config/haulbacks` and `/config/route-roadblocks` are fetched using
 * the currently selected app language, and their entries are matched to
 * submission rows strictly by `id` (never by array index/position).
 */

import { HaulbackResponseDTO, RouteRoadblockResponseDTO } from '../../api/deliveryRpmApi';
import { HaulbackDtoEntry } from '../../store/slices/deliveryRpmSlice';

export interface HaulbackReasonMaps {
  /** id → reasonCode label, translated for the requested language */
  rcMap: Record<number, string>;
  /** id → full catalogue entry (for pictureRequired / textFieldRequired flags) */
  dtoMap: Record<number, HaulbackDtoEntry>;
}

/** Builds id → reasonCode lookup maps from a `/config/haulbacks` response. */
export function buildHaulbackReasonMaps(items: HaulbackResponseDTO[]): HaulbackReasonMaps {
  const rcMap: Record<number, string> = {};
  const dtoMap: Record<number, HaulbackDtoEntry> = {};
  for (const item of items) {
    rcMap[item.id] = item.reasonCode;
    dtoMap[item.id] = {
      reasonCode: item.reasonCode,
      pictureRequired: item.pictureRequired,
      textFieldRequired: item.textFieldRequired,
    };
  }
  return { rcMap, dtoMap };
}

export interface RoadblockReasonMaps {
  /** id → routeRoadblock label, translated for the requested language */
  rcMap: Record<number, string>;
  /** id → roadblockType label ('Stop Issue' | 'Route Issue'), translated for the requested language */
  typeMap: Record<number, string>;
}

/** Builds id → routeRoadblock / id → roadblockType lookup maps from a `/config/route-roadblocks` response. */
export function buildRoadblockReasonMaps(items: RouteRoadblockResponseDTO[]): RoadblockReasonMaps {
  const rcMap: Record<number, string> = {};
  const typeMap: Record<number, string> = {};
  for (const item of items) {
    rcMap[item.id] = item.routeRoadblock;
    typeMap[item.id] = item.roadblockType;
  }
  return { rcMap, typeMap };
}

/**
 * Resolves the translated display label for a submission's `reasonCodeId`
 * against an id → label map, falling back to the raw id when no config
 * entry matches (e.g. a stale/removed reason code).
 */
export function resolveReasonLabel(reasonCodeId: number, map: Record<number, string>): string {
  return map[reasonCodeId] ?? String(reasonCodeId);
}
